import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Box, Typography, Grid, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useVehicleReservation } from "../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useStoreDetails } from "../../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { useStepper } from "../../../../common/Providers/StepperProvider/useStepper";
import { useResponsiveValues } from "../../../../common/Providers/DimentionsProvider/UseResponsiveValues";
import { getProductFamiliesData, getBusyPeriods, getVehicleRentalProdLocation } from "../../../../api/Product";
import ProductCard from "./ProductCard";
import DateSelector from "./DateSelector";
import DurationSelector from "./DurationSelector";
import RentalHeader from "../../../../components/rental/RentalHeader";
import SummaryCard from "../../SummaryCard";
import TimeSlotSelector from "./TimeSlotSelector";
import FloatingActionButton from "../../../FloatingActionButton";
import StepperLayout from "../StepperLayout";
import { formatDateString } from "../../../../common/Utils";
import { Alert } from "@mui/material";
import CategorySelector from "../../CategorySelector";
import { useManageKeys } from "../../../../common/Providers/ManageKeysProvider/UseManageKeys";
import Editable from "../../../../cms/Editable";

const ScheduleStep: React.FC = () => {
  const { next, back, isFirstStep } = useStepper();
  const { VehicleReservationMain, VehicleReservationItems, setVehicleReservationValue, updatePricingForSelectedDuration } = useVehicleReservation();
  const { storeDetails, storeContent, showLoader, hideLoader, hasAutoProceeded, setHasAutoProceeded } = useStoreDetails();
  const { matches900 } = useResponsiveValues();
  const { getKey, getKeyItem } = useManageKeys();

  // --- Product state ---
  const [products, setProducts] = useState<Array<any>>([]);
  const [inventoryMessage, setInventoryMessage] = useState<any>(null);

  // --- API response state (single source of truth) ---
  const [apiData, setApiData] = useState<any>(null);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);

  // --- Selection state ---
  const [selectedDurationObj, setSelectedDurationObj] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<any>(null);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);

  // --- Refs ---
  const previousPayloadRef = useRef<string | null>(null);
  const durationSelectorRef = useRef<HTMLDivElement>(null);
  const timeSlotSelectorRef = useRef<HTMLDivElement>(null);
  const cameBackRef = useRef<boolean>(hasAutoProceeded);
  const userHasNavigatedBackRef = useRef<boolean>(false);

  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlTime = urlParams.get('time'); // e.g. "09:30" from QR redirect

  const isQuickOrder = storeDetails.isQuickOrder;
  const isRentalFlow = storeDetails.isRentalFlow;

  // --- Derived state ---
  const isItemSelected = VehicleReservationItems.length > 0;
  const isDateSelected = Boolean(VehicleReservationMain.pickup);
  
  // Calculate max quantity per product (not total cart length)
  // Group items by family_id and count each group
  const requestedQuantity = useMemo(() => {
    if (VehicleReservationItems.length === 0) return 1;
    
    const quantityMap: { [key: number]: number } = {};
    VehicleReservationItems.forEach((item: any) => {
      quantityMap[item.family_id] = (quantityMap[item.family_id] || 0) + 1;
    });
    
    // Return the maximum quantity across all products
    return Math.max(...Object.values(quantityMap));
  }, [VehicleReservationItems]);

  // Get the selected date's data from API response
  const selectedDateData = useMemo(() => {
    if (!apiData?.dates || !VehicleReservationMain.pickup) return null;
    const dateStr = formatDateString(VehicleReservationMain.pickup as Date);
    return apiData.dates.find((d: any) => d.date === dateStr) || null;
  }, [apiData, VehicleReservationMain.pickup]);

  const hasSelectedDuration = Boolean(selectedDurationObj);

  // Check if there are any available durations for the selected date
  const hasAvailableDurations = useMemo(() => {
    if (!selectedDateData?.durations) return false;
    return selectedDateData.durations.some((d: any) => d.is_available);
  }, [selectedDateData]);

  const isCartInvalid: boolean = useMemo(() => {
    if (VehicleReservationMain.availableSheet && VehicleReservationItems.length > 0) {
      const quantityMap = VehicleReservationItems.reduce((acc, item) => {
        acc[item.family_id] = (acc[item.family_id] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });

      return Object.keys(quantityMap).some(family_id => {
        const item = VehicleReservationItems.find(i => i.family_id.toString() === family_id);
        if (!item) return false;
        const availableQuantity = VehicleReservationMain.availableSheet[item.display_name] ?? 0;
        return quantityMap[family_id] > availableQuantity;
      });
    }
    return false;
  }, [VehicleReservationItems, VehicleReservationMain.availableSheet]);

  const isContinueButtonVisible = useMemo(() => {
    if (isQuickOrder) {
      // Quick order: time is pre-set from URL, availability already verified by check-availability API
      // Only need item + date + duration selected (dropoff is auto-calculated on duration select)
      const hasDropoff = Boolean(VehicleReservationMain.dropoff);
      console.log('🔘 isContinueButtonVisible (quick order):', {
        isItemSelected, isDateSelected, hasAvailableDurations,
        selectedDurationObj: Boolean(selectedDurationObj),
        hasDropoff,
        dropoff: VehicleReservationMain.dropoff,
      });
      return isItemSelected && isDateSelected && hasAvailableDurations && Boolean(selectedDurationObj) && hasDropoff;
    }
    // Normal flow: time slot selection required
    const isTimeSlotValid = selectedTimeSlot && selectedTimeSlot.available === true;
    return isItemSelected && isDateSelected && hasAvailableDurations && isTimeSlotValid && !isCartInvalid && Boolean(VehicleReservationMain.availableSheet);
  }, [isQuickOrder, isItemSelected, isDateSelected, hasAvailableDurations, selectedTimeSlot, selectedDurationObj, VehicleReservationMain.availableSheet, VehicleReservationMain.dropoff, isCartInvalid]);

  // --- API call helper ---
  const fetchAvailability = useCallback(async (startDate: string, endDate: string, selectedDate?: string) => {
    if (!isItemSelected) return;

    let apiOrderType = '';
    switch (VehicleReservationMain.orderType) {
      case 'ride_off': apiOrderType = 'Pickup'; break;
      case 'delivery': apiOrderType = 'Delivery'; break;
    }

    // Get ALL items in cart to show merged availability
    // This ensures when user adds a second product, they immediately see only slots that work for BOTH
    if (VehicleReservationItems.length === 0) return;

    // Get unique product families and their quantities from ALL cart items
    const productQuantities: { [key: number]: number } = {};
    VehicleReservationItems.forEach((item: any) => {
      productQuantities[item.family_id] = (productQuantities[item.family_id] || 0) + 1;
    });

    const uniqueProductIds = Object.keys(productQuantities).map(Number);
    
    // Fallback selectedDate to today if not provided
    const effectiveSelectedDate = selectedDate || dayjs().format('YYYY-MM-DD');

    // For single product selection, call API for that product only
    // For multiple products, we still call separately but will show individual availability
    const payloadSignature = JSON.stringify({
      start_date: startDate,
      end_date: endDate,
      selected_date: effectiveSelectedDate,
      brand_id: storeDetails.brand_id,
      product_ids: uniqueProductIds.sort(),
      quantities: uniqueProductIds.map(id => productQuantities[id]).sort(),
      order_type: apiOrderType,
      use_manual: VehicleReservationMain.use_manual,
      address_id: VehicleReservationMain.address_id,
      plantation_id: VehicleReservationMain.plantation_id,
    });
    
    if (previousPayloadRef.current === payloadSignature) return;
    previousPayloadRef.current = payloadSignature;

    setIsApiLoading(true);
    
    // Call API separately for each product
    const apiCalls = uniqueProductIds.map(productId => {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        brand_id: storeDetails.brand_id,
        product_ids: [productId],
        quantity: productQuantities[productId],
        order_type: apiOrderType,
        category_id: VehicleReservationMain.mainCategory || null,
        // Address context: pass address_id + plantation_id if user selected from list,
        // or use_manual=1 if they typed a manual address
        use_manual: VehicleReservationMain.use_manual ? 1 : 0,
        address_id: VehicleReservationMain.use_manual ? null : (VehicleReservationMain.address_id || null),
        plantation_id: VehicleReservationMain.plantation_id || null,
      };
      
      console.log(`🔍 API Request for Product ${productId}:`, payload);
      
      return new Promise((resolve) => {
        getBusyPeriods({ ...payload, selected_date: effectiveSelectedDate }, (jsonRes: any, status: any) => {
          console.log(`📡 API Response for Product ${productId}:`, jsonRes);
          if (status === 200 && jsonRes?.success) {
            resolve({ productId, data: jsonRes });
          } else {
            resolve({ productId, data: null });
          }
        });
      });
    });

    // Wait for all API calls to complete
    const results = await Promise.all(apiCalls);
    
    // If only one product, use its data directly (no merging needed)
    // If multiple products of SAME family, use that family's data
    // If multiple DIFFERENT products, merge to show only slots available for ALL
    const firstResult = results.find((r: any) => r.data) as any;
    
    if (!firstResult?.data) {
      setApiData(null);
      setIsApiLoading(false);
      return;
    }
    
    // Check if all products are the same family
    const allSameFamily = uniqueProductIds.length === 1;
    
    if (allSameFamily) {
      // Single product family - show its availability directly
      console.log(`📊 Single Product Family ${uniqueProductIds[0]} - showing direct availability`);
      setApiData(firstResult.data);
      
      // Check if currently selected time slot is still available in new API data
      if (selectedTimeSlot && VehicleReservationMain.pickup) {
        const dateStr = formatDateString(VehicleReservationMain.pickup as Date);
        const dateData = firstResult.data.dates?.find((d: any) => d.date === dateStr);
        
        if (dateData && selectedDurationObj) {
          const durationData = dateData.durations?.find((d: any) => 
            d.duration_value === selectedDurationObj.duration_value && 
            d.duration_type === selectedDurationObj.duration_type
          );
          
          if (durationData?.available_time_slots) {
            const currentSlot = durationData.available_time_slots.find(
              (s: any) => s.time_24h === selectedTimeSlot.time_24h
            );
            
            if (!currentSlot || !currentSlot.available) {
              console.warn(`⚠️ Previously selected time slot ${selectedTimeSlot.time_12h} is no longer available. Clearing selection.`);
              setSelectedTimeSlot(null);
              setSelectedDurationObj(null);
              setVehicleReservationValue("pickup_time", null);
              setVehicleReservationValue("selectedDuration", null);
              setVehicleReservationValue("dropoff", null);
              setVehicleReservationValue("dropoff_time", null);
              setConflictWarning(`The time slot ${selectedTimeSlot.time_12h} is not available for this product. Please select a new duration and time.`);
            }
          }
        }
      }
      
      // Log time slots for debugging
      if (firstResult.data.dates && firstResult.data.dates.length > 0) {
        firstResult.data.dates.forEach((date: any) => {
          console.log(`📅 Date: ${date.date}, Available: ${date.is_available}`);
          if (date.durations) {
            date.durations.forEach((dur: any) => {
              console.log(`  ⏱️ Duration: ${dur.duration_value} ${dur.duration_type}, Available: ${dur.is_available}`);
              if (dur.available_time_slots) {
                console.log(`    Time slots:`, dur.available_time_slots.map((s: any) => 
                  `${s.time_12h}: ${s.available ? '✅' : '❌'}`
                ).join(', '));
              }
            });
          }
        });
      }
    } else {
      // Multiple different product families - merge availability
      // A slot is available only if ALL products can be booked at that time
      console.log(`📊 Multiple Product Families - merging availability for:`, uniqueProductIds);
      
      let mergedData: any = null;
      
      for (const result of results) {
        const { productId, data } = result as any;
        
        if (!data) continue;
        
        if (!mergedData) {
          mergedData = { ...data, _products: [productId] };
          continue;
        }
        
        mergedData._products.push(productId);
        
        if (data.dates && mergedData.dates) {
          mergedData.dates = mergedData.dates.map((mergedDate: any) => {
            const dataDate = data.dates.find((d: any) => d.date === mergedDate.date);
            if (!dataDate) return { ...mergedDate, is_available: false };
            
            const isAvailable = mergedDate.is_available && dataDate.is_available;
            const remainingQuantity = Math.min(
              mergedDate.remaining_quantity || 0,
              dataDate.remaining_quantity || 0
            );
            
            const durations = mergedDate.durations?.map((mergedDur: any) => {
              const dataDur = dataDate.durations?.find((d: any) => 
                d.duration_value === mergedDur.duration_value && 
                d.duration_type === mergedDur.duration_type
              );
              
              if (!dataDur) return { ...mergedDur, is_available: false };
              
              const durAvailable = mergedDur.is_available && dataDur.is_available;
              
              const timeSlots = mergedDur.available_time_slots?.map((mergedSlot: any) => {
                const dataSlot = dataDur.available_time_slots?.find((s: any) => 
                  s.time_24h === mergedSlot.time_24h
                );
                
                if (!dataSlot) return { ...mergedSlot, available: false };
                
                return {
                  ...mergedSlot,
                  available: mergedSlot.available && dataSlot.available,
                  rental_slots: [...(mergedSlot.rental_slots || []), ...(dataSlot.rental_slots || [])],
                  buffer_slots: [...(mergedSlot.buffer_slots || []), ...(dataSlot.buffer_slots || [])],
                };
              });
              
              return {
                ...mergedDur,
                is_available: durAvailable,
                available_time_slots: timeSlots,
              };
            });
            
            return {
              ...mergedDate,
              is_available: isAvailable,
              remaining_quantity: remainingQuantity,
              durations,
            };
          });
        }
      }
      
      if (mergedData) {
        console.log('🔀 Merged API Data:', mergedData);
        setApiData(mergedData);
        
        // Check if currently selected time slot is still available in merged data
        if (selectedTimeSlot && VehicleReservationMain.pickup) {
          const dateStr = formatDateString(VehicleReservationMain.pickup as Date);
          const dateData = mergedData.dates?.find((d: any) => d.date === dateStr);
          
          if (dateData && selectedDurationObj) {
            const durationData = dateData.durations?.find((d: any) => 
              d.duration_value === selectedDurationObj.duration_value && 
              d.duration_type === selectedDurationObj.duration_type
            );
            
            if (durationData?.available_time_slots) {
              const currentSlot = durationData.available_time_slots.find(
                (s: any) => s.time_24h === selectedTimeSlot.time_24h
              );
              
              if (!currentSlot || !currentSlot.available) {
                console.warn(`⚠️ Previously selected time slot ${selectedTimeSlot.time_12h} is no longer available for all products. Clearing selection.`);
                setSelectedTimeSlot(null);
                setSelectedDurationObj(null);
                setVehicleReservationValue("pickup_time", null);
                setVehicleReservationValue("selectedDuration", null);
                setVehicleReservationValue("dropoff", null);
                setVehicleReservationValue("dropoff_time", null);
                setConflictWarning(`The time slot ${selectedTimeSlot.time_12h} is not available for all selected products. Please select a new duration and time.`);
              }
            }
          }
        }
        
        if (mergedData.dates && mergedData.dates.length > 0) {
          mergedData.dates.forEach((date: any) => {
            console.log(`📅 Date: ${date.date}, Available: ${date.is_available}`);
            if (date.durations) {
              date.durations.forEach((dur: any) => {
                console.log(`  ⏱️ Duration: ${dur.duration_value} ${dur.duration_type}, Available: ${dur.is_available}`);
                if (dur.available_time_slots) {
                  console.log(`    Time slots:`, dur.available_time_slots.map((s: any) => 
                    `${s.time_12h}: ${s.available ? '✅' : '❌'}`
                  ).join(', '));
                }
              });
            }
          });
        }
      } else {
        setApiData(null);
      }
    }
    
    setIsApiLoading(false);
  }, [isItemSelected, VehicleReservationMain.orderType, storeDetails.brand_id, VehicleReservationItems, VehicleReservationMain.use_manual, VehicleReservationMain.address_id, VehicleReservationMain.plantation_id]);

  // --- Load products ---
  useEffect(() => {
    if (VehicleReservationMain.mainCategory) {
      showLoader();
      setInventoryMessage(null);
      getVehicleRentalProdLocation(
        { categoryId: VehicleReservationMain.mainCategory, brandId: storeDetails.brand_id },
        (jsonRes: any, status: any) => {
          let hasProducts = false;
          if (status === 200 && Array.isArray(jsonRes) && jsonRes.length) {
            const filteredFamilies = jsonRes.filter((item: any) => {
              if (!item.brand_ids) return false;
              const brandsIds = (item.brand_ids || "").split("-");
              const brandsIdsArr = brandsIds.map((item: any) => JSON.parse(item)).flat();
              return brandsIdsArr.includes(storeDetails.brand_id);
            });
            setProducts(filteredFamilies);
            hasProducts = filteredFamilies.length > 0;
          } else {
            setProducts([]);
          }
          
          if (!hasProducts && jsonRes && jsonRes.error) {
            setInventoryMessage(jsonRes);
          }
          
          hideLoader();
        }
      );
    } else {
      setProducts([]);
      setInventoryMessage(null);
    }
  }, [VehicleReservationMain.mainCategory, storeDetails.brand_id]);

  // --- Initial API call when items are selected ---
  useEffect(() => {
    if (isItemSelected) {
      const today = dayjs();
      const viewMonth = VehicleReservationMain.pickup ? dayjs(VehicleReservationMain.pickup) : today;
      const isCurrentMonth = viewMonth.isSame(today, 'month');

      // Current month: today → end of month | Other months: 1st → last of month
      const startDate = isCurrentMonth
        ? today.format('YYYY-MM-DD')
        : viewMonth.startOf('month').format('YYYY-MM-DD');
      const endDate = viewMonth.endOf('month').format('YYYY-MM-DD');

      // selectedDate = pickup date if set, otherwise today
      const selectedDate = VehicleReservationMain.pickup
        ? dayjs(VehicleReservationMain.pickup).format('YYYY-MM-DD')
        : today.format('YYYY-MM-DD');

      fetchAvailability(startDate, endDate, selectedDate);
    } else {
      setApiData(null);
      previousPayloadRef.current = null;
    }
  }, [VehicleReservationItems, storeDetails.brand_id]);

  // Re-fetch when address selection changes (address_id or use_manual changes)
  useEffect(() => {
    if (isItemSelected) {
      const today = dayjs();
      const viewMonth = VehicleReservationMain.pickup ? dayjs(VehicleReservationMain.pickup) : today;
      const isCurrentMonth = viewMonth.isSame(today, 'month');

      const startDate = isCurrentMonth
        ? today.format('YYYY-MM-DD')
        : viewMonth.startOf('month').format('YYYY-MM-DD');
      const endDate = viewMonth.endOf('month').format('YYYY-MM-DD');

      const selectedDate = VehicleReservationMain.pickup
        ? dayjs(VehicleReservationMain.pickup).format('YYYY-MM-DD')
        : today.format('YYYY-MM-DD');

      previousPayloadRef.current = null; // bust the cache
      fetchAvailability(startDate, endDate, selectedDate);
    }
  }, [VehicleReservationMain.use_manual, VehicleReservationMain.address_id, VehicleReservationMain.plantation_id]);

  // Track the selected date string for reset cascade
  const selectedDateStr = VehicleReservationMain.pickup ? formatDateString(VehicleReservationMain.pickup as Date) : null;
  const prevDateRef = useRef<string | null>(null);

  // --- RESET CASCADE ---
  // When date changes → reset duration and time
  useEffect(() => {
    if (prevDateRef.current !== null && prevDateRef.current !== selectedDateStr) {
      setSelectedDurationObj(null);
      setSelectedTimeSlot(null);
      setVehicleReservationValue("selectedDuration", null);
      setVehicleReservationValue("pickup_time", null);
      setVehicleReservationValue("dropoff", null);
      setVehicleReservationValue("dropoff_time", null);
    }
    prevDateRef.current = selectedDateStr;
  }, [selectedDateStr]);

  // --- Scroll to duration selector when date is selected ---
  useEffect(() => {
    if (isDateSelected && !isQuickOrder && durationSelectorRef.current) {
      setTimeout(() => {
        durationSelectorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isDateSelected]);

  // --- Scroll to time slot selector when duration is selected ---
  useEffect(() => {
    if (hasSelectedDuration && !isQuickOrder && timeSlotSelectorRef.current) {
      setTimeout(() => {
        timeSlotSelectorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [hasSelectedDuration, isQuickOrder]);

  // --- Restore selected duration and time slot when coming back to this step ---
  useEffect(() => {
    if (!selectedDurationObj && VehicleReservationMain.selectedDuration && selectedDateData?.durations) {
      // Find the duration object from API data that matches the saved selectedDuration
      const savedDurationId = VehicleReservationMain.selectedDuration;
      const matchingDuration = selectedDateData.durations.find((d: any) => {
        let id = '';
        if (d.duration_type === 'hourly') {
          id = `${d.duration_value}-hour`;
        } else if (d.duration_type === 'daily') {
          id = `${d.duration_value}-day`;
        } else if (d.duration_type === 'weekly') {
          id = `${d.duration_value}-week`;
        } else if (d.duration_type === 'monthly') {
          id = `${d.duration_value}-month`;
        }
        return id === savedDurationId;
      });

      if (matchingDuration) {
        // Format the duration object to match what DurationSelector expects
        const formattedDuration = {
          ...matchingDuration,
          id: savedDurationId,
          name: matchingDuration.duration_label || `${matchingDuration.duration_value} ${matchingDuration.duration_type}`,
          price: parseFloat(matchingDuration.price) || 0,
        };
        setSelectedDurationObj(formattedDuration);

        // If there's a saved pickup_time, restore the time slot ONLY if it's available
        if (VehicleReservationMain.pickup_time && matchingDuration.available_time_slots) {
          const savedTime = VehicleReservationMain.pickup_time;
          const matchingTimeSlot = matchingDuration.available_time_slots.find(
            (slot: any) => slot.time_24h?.startsWith(savedTime)
          );
          // Only restore if the slot exists AND is marked as available
          if (matchingTimeSlot && matchingTimeSlot.available) {
            setSelectedTimeSlot(matchingTimeSlot);
          } else if (matchingTimeSlot && !matchingTimeSlot.available) {
            // Slot exists but is unavailable - clear the saved time
            console.warn(`⚠️ Saved time slot ${savedTime} is no longer available. Clearing selection.`);
            setSelectedTimeSlot(null);
            setVehicleReservationValue("pickup_time", null);
          }
        }
      }
    }
  }, [selectedDateData, VehicleReservationMain.selectedDuration, VehicleReservationMain.pickup_time, selectedDurationObj]);

  // --- Handle duration selection ---
  const handleDurationSelect = useCallback((durationData: any) => {
    if (!durationData) {
      setSelectedDurationObj(null);
      setSelectedTimeSlot(null);
      setVehicleReservationValue("selectedDuration", null);
      setVehicleReservationValue("dropoff", null);
      setVehicleReservationValue("dropoff_time", null);
      setVehicleReservationValue("pickup_time", null);
      return;
    }

    // Reset time selection when duration changes
    setSelectedTimeSlot(null);
    setVehicleReservationValue("pickup_time", null);

    setSelectedDurationObj(durationData);

    // Build duration ID string for global state
    let durationId = '';
    if (durationData.duration_type === 'hourly') {
      durationId = `${durationData.duration_value}-hour`;
    } else if (durationData.duration_type === 'daily') {
      durationId = `${durationData.duration_value}-day`;
    } else if (durationData.duration_type === 'weekly') {
      durationId = `${durationData.duration_value}-week`;
    } else if (durationData.duration_type === 'monthly') {
      durationId = `${durationData.duration_value}-month`;
    }
    setVehicleReservationValue("selectedDuration", durationId);

    // Store upcharge value if it exists
    if (durationData.upcharge && durationData.upcharge > 0) {
      setVehicleReservationValue("upcharge", durationData.upcharge);
      setVehicleReservationValue("upcharge_plantation", durationData.upcharge_plantation || null);
      console.log(`💰 Upcharge stored: $${durationData.upcharge}${durationData.upcharge_plantation ? ` (${durationData.upcharge_plantation})` : ''}`);
    } else {
      setVehicleReservationValue("upcharge", null);
      setVehicleReservationValue("upcharge_plantation", null);
    }

    // Update pricing with actual duration data from new API
    updatePricingForSelectedDuration(durationId, durationData);

    // For quick order + hourly: auto-calculate dropoff from URL time param (time selector is hidden)
    // Use urlTime directly from URL since pickup_time state may not be set yet (async from Rental.tsx)
    const quickOrderTime = urlTime || VehicleReservationMain.pickup_time;
    if (isQuickOrder && durationData.duration_type === 'hourly' && quickOrderTime && VehicleReservationMain.pickup) {
      const [hours, minutes] = quickOrderTime.split(':').map(Number);
      const pickupDate = new Date(VehicleReservationMain.pickup);
      pickupDate.setHours(hours, minutes, 0, 0);

      const durationMinutes = durationData.duration_minutes || (durationData.duration_value * 60);
      const dropoffDate = dayjs(pickupDate).add(durationMinutes, 'minute');
      const dropoffTimeStr = dropoffDate.format('HH:mm');

      setVehicleReservationValue("pickup", pickupDate);
      setVehicleReservationValue("pickup_time", quickOrderTime);
      setVehicleReservationValue("dropoff", new Date(dropoffDate.toISOString()));
      setVehicleReservationValue("dropoff_time", dropoffTimeStr);
      setHasAutoProceeded(false);
    }

    // For daily/weekly/monthly, set dropoff immediately (no time picker)
    if (['daily', 'weekly', 'monthly'].includes(durationData.duration_type)) {
      if (VehicleReservationMain.pickup) {
        let dropoffDate;
        if (durationData.duration_type === 'monthly') {
          dropoffDate = dayjs(VehicleReservationMain.pickup).add(durationData.duration_value, 'month');
        } else {
          let days = durationData.duration_value;
          if (durationData.duration_type === 'weekly') days = durationData.duration_value * 7;
          dropoffDate = dayjs(VehicleReservationMain.pickup).add(days, 'day');
        }
        const dropoffDateTime = new Date(dropoffDate.toISOString());

        // Daily return time from API or default 09:00
        const returnTime = apiData?.daily_return_time || '09:00';
        const [rh, rm] = returnTime.split(':').map(Number);
        dropoffDateTime.setHours(rh, rm, 0, 0);

        setVehicleReservationValue("dropoff", dropoffDateTime);
        setVehicleReservationValue("dropoff_time", returnTime);

        // Set a default pickup time for daily rentals
        const businessStart = apiData?.business_hours?.start || '9:00 AM';
        setVehicleReservationValue("pickup_time", businessStart);

        // Set pickup datetime with business start time (convert 12-hour to 24-hour for Date object)
        const pickupDate = new Date(VehicleReservationMain.pickup);
        const businessStartParsed = dayjs(`2000-01-01 ${businessStart}`, 'YYYY-MM-DD h:mm A');
        pickupDate.setHours(businessStartParsed.hour(), businessStartParsed.minute(), 0, 0);
        setVehicleReservationValue("pickup", pickupDate);
      }
      setHasAutoProceeded(false);
    }
  }, [isQuickOrder, urlTime, VehicleReservationMain.pickup, VehicleReservationMain.pickup_time, apiData, setVehicleReservationValue, updatePricingForSelectedDuration, setHasAutoProceeded]);

  // --- Handle time slot selection ---
  const handleTimeSelect = useCallback((slotData: any) => {
    if (!slotData || !VehicleReservationMain.pickup) return;

    setSelectedTimeSlot(slotData);

    // Parse the time
    const time24h = slotData.time_24h; // e.g. "09:00:00"
    const [hours, minutes] = time24h.split(':').map(Number);

    // Set pickup time
    const pickupDate = new Date(VehicleReservationMain.pickup);
    pickupDate.setHours(hours, minutes, 0, 0);
    const sqlTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    setVehicleReservationValue("pickup", pickupDate);
    setVehicleReservationValue("pickup_time", sqlTime);

    // Calculate dropoff for hourly durations
    if (selectedDurationObj && selectedDurationObj.duration_type === 'hourly') {
      const durationMinutes = selectedDurationObj.duration_minutes || (selectedDurationObj.duration_value * 60);
      const dropoffDate = dayjs(pickupDate).add(durationMinutes, 'minute');
      const dropoffDateTime = new Date(dropoffDate.toISOString());

      const dropoffTimeStr = dropoffDate.format('HH:mm');
      setVehicleReservationValue("dropoff", dropoffDateTime);
      setVehicleReservationValue("dropoff_time", dropoffTimeStr);
    }

    setHasAutoProceeded(false);
  }, [VehicleReservationMain.pickup, selectedDurationObj, setVehicleReservationValue, setHasAutoProceeded]);

  // --- Month change handler for DateSelector ---
  const handleMonthChange = useCallback((dateRange: { start: Date; end: Date; selectedDate?: string }) => {
    if (isItemSelected) {
      fetchAvailability(formatDateString(dateRange.start), formatDateString(dateRange.end), dateRange.selectedDate);
    }
  }, [isItemSelected, fetchAvailability]);

  // --- Detect when user navigates back ---
  useEffect(() => {
    // If we have items and all selections are already made, user likely came back
    if (isItemSelected && isDateSelected && selectedDurationObj && selectedTimeSlot) {
      userHasNavigatedBackRef.current = true;
    }
  }, []); // Only run on mount

  // --- Auto-proceed ---
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

  useEffect(() => {
    if (isContinueButtonVisible && !hasAutoProceeded && !cameBackRef.current && !userHasNavigatedBackRef.current) {
      setHasAutoProceeded(true);
      (async () => {
        showLoader();
        await delay(2000);
        hideLoader();
        next();
      })();
    }
  }, [isContinueButtonVisible, hasAutoProceeded]);

  // --- Disabled dates for calendar ---
  const disabledDates = useMemo(() => {
    if (!apiData?.dates) return [];
    // Only use is_available flag - API already accounts for partial-day availability
    return apiData.dates
      .filter((d: any) => !d.is_available)
      .map((d: any) => d.date);
  }, [apiData]);

  return (
    <Box>
      <StepperLayout
        rightContent={
          <SummaryCard
            isCheckoutStage={false}
            showDiscount={false}
          />
        }
      >
        <RentalHeader title={storeDetails.store_name} showActionButton={!isFirstStep} onActionClick={() => back()} primaryColor={storeContent.primary_color} />
        <Box sx={styles.contentContainer}>
          <Box>
            {(() => {
              const item = getKeyItem('right_panel_text');
              return (
                <Editable keyName="right_panel_text" fieldType="html" label="Right Panel Text" value={item?.value || ''}>
                  {item?.content_type === 'html' ? (
                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: item.value }} />
                  ) : (
                    <Typography sx={styles.contentTitle}>
                      <em>{getKey('right_panel_text', storeContent.schedule_step_description || 'Get your Moke to directly to your off-island location!')}</em>
                    </Typography>
                  )}
                </Editable>
              );
            })()}
          </Box>

          {!isQuickOrder && !isRentalFlow && (
            <Box>
              <Typography variant="h4" sx={styles.sectionTitle}>
                {getKey('select_category', 'Select Category')}
              </Typography>
              <CategorySelector primaryColor={storeContent.primary_color} />
            </Box>
          )}

          {!isQuickOrder && !isRentalFlow && (
            <Box>
              {inventoryMessage && (
                <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {inventoryMessage.error}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {inventoryMessage.message}
                  </Typography>
                </Alert>
              )}
              {products.length > 0 && (
                <>
                  <Typography variant="h4" sx={styles.sectionTitle}>
                    {getKey('select_your_rental', 'Select Your Rental')}
                  </Typography>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {products.map((product: any, index: number) => (
                      <Grid item xs={6} sm={6} md={6} lg={6} key={product.id + "_" + index}>
                        <ProductCard key={product.id + "_" + index} product={product} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          )}

          {/* STEP 2: SELECT DATE — hidden for quick order (date already pre-set from QR redirect) */}
          {isItemSelected && !isQuickOrder && (
            <Box>
              <Typography variant="h4" sx={styles.sectionTitle}>
                {getKey('select_date', 'Select Date')}
              </Typography>
              {/* Debug info */}
              {apiData && (
                <Box sx={{ mb: 1, p: 1, backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                  <strong>Info:</strong> Business Hours: {apiData.business_hours?.start || 'N/A'} - {apiData.business_hours?.end || 'N/A'} | 
                  {apiData.business_hours?.cut_off && `Cut-off: ${apiData.business_hours.cut_off} | `}
                  Slot Interval: {apiData.business_hours?.slot_interval_minutes || 'N/A'} min | 
                  Buffer: {apiData.buffer_minutes || 'N/A'} min 
                </Box>
              )}
              {!apiData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <>
                  {isApiLoading && (
                    <Box sx={{
                      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
                    }}>
                      <CircularProgress size={48} sx={{ color: '#fff' }} />
                    </Box>
                  )}
                  <DateSelector
                    disabledDates={disabledDates}
                    onMonthChange={handleMonthChange}
                    minSelectableDate={apiData.min_selectable_date}
                    currentDate={apiData.current_date}
                  />
                </>
              )}
            </Box>
          )}

          {/* STEP 3: SELECT DURATION (shown after date) */}
          {isDateSelected && selectedDateData && (
            <Box ref={durationSelectorRef}>
              <Editable keyName="schedule_step_enddatelabel" fieldType="text" label="Schedule Step End Date Label" value={storeContent.schedule_step_enddatelabel || ''}>
                <Typography variant="h4" sx={styles.sectionTitle}>
                  {getKey('select_duration', storeContent.schedule_step_enddatelabel || 'Select Duration')}
                </Typography>
              </Editable>
              
              {/* Show conflict warning if exists */}
              {conflictWarning && (
                <Alert 
                  severity="error" 
                  onClose={() => setConflictWarning(null)}
                  sx={{ mb: 2 }}
                >
                  {conflictWarning}
                </Alert>
              )}
              
              {isApiLoading ? (
                <Box sx={{ height: 52, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <DurationSelector
                  dateData={selectedDateData}
                  apiData={apiData}
                  onDurationSelect={handleDurationSelect}
                  selectedDurationObj={selectedDurationObj}
                />
              )}
            </Box>
          )}

          {/* STEP 4: SELECT TIME — hidden for quick order (time already pre-set from QR redirect) */}
          {isDateSelected && hasSelectedDuration && hasAvailableDurations && !isQuickOrder && (
            <Box ref={timeSlotSelectorRef}>
              <Typography variant="h4" sx={styles.sectionTitle}>
                {getKey('select_time', 'Select Time')}
              </Typography>
              {isApiLoading ? (
                <Box sx={{ height: 52, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TimeSlotSelector
                  durationData={selectedDurationObj}
                  dateData={selectedDateData}
                  onTimeSelect={handleTimeSelect}
                  selectedTimeSlot={selectedTimeSlot}
                />
              )}
              
              {/* Show return info for daily/weekly/monthly rentals */}
              {selectedTimeSlot?.return_info && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e3f2fd', borderRadius: '8px', backgroundColor: '#f5f9ff' }}>
                  <Typography sx={{ fontSize: '14px', color: '#1976d2', fontWeight: 500 }}>
                    📅 {selectedTimeSlot.return_info}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {isQuickOrder && VehicleReservationMain.no_available_time_slots && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error">
                No available time slots found for today. All time slots are currently booked. Please try ordering for a later date using "Order Later" option.
              </Alert>
            </Box>
          )}

          {isCartInvalid && hasSelectedDuration && selectedTimeSlot && VehicleReservationMain.availableSheet && (
            <Box>
              <Alert severity="error">
                It's not available. Please try another time slot or day.
              </Alert>
            </Box>
          )}
        </Box>
      </StepperLayout>

      {/* Floating Continue Button */}
      <FloatingActionButton label="Continue" primaryColor={storeContent.primary_color} onClick={() => next()} visible={isContinueButtonVisible} disabled={!isContinueButtonVisible} />
    </Box>
  );
};

const styles = {
  contentContainer: {
    flex: 1,
    p: 1.5,
    backgroundColor: "white",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    marginBottom: "40px",
  },
  contentTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  sectionTitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  }
};

export default ScheduleStep;
